import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

/** The article card LinkedIn draws for a link: what the publisher will send as the card. */
export interface LinkedInLinkPreview {
  domain: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
}

export function parseLinkedInLinkPreview(value: unknown): LinkedInLinkPreview | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (v.state !== 'ok' || typeof v.domain !== 'string' || !v.domain) return null;
  const text = (x: unknown) => (typeof x === 'string' && x.trim() ? x : null);
  return {
    domain: v.domain,
    title: text(v.title),
    description: text(v.description),
    imageUrl:
      typeof v.imageUrl === 'string' && /^https?:\/\//i.test(v.imageUrl) ? v.imageUrl : null,
  };
}

class LinkedInLinkPreviewService extends BaseService {
  private static instance: LinkedInLinkPreviewService;

  private constructor() {
    super({ defaultTTL: 0, enableCache: false, enableRetry: false });
  }

  static getInstance(): LinkedInLinkPreviewService {
    if (!LinkedInLinkPreviewService.instance) {
      LinkedInLinkPreviewService.instance = new LinkedInLinkPreviewService();
    }
    return LinkedInLinkPreviewService.instance;
  }

  async fetchPreview(url: string): Promise<ServiceResponse<LinkedInLinkPreview | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client.functions.invoke('linkedin-link-preview', {
        body: { url },
      });
      if (error) throw error;
      return parseLinkedInLinkPreview(data);
    }, 'Failed to read the link preview');
  }
}

export const linkedInLinkPreviewService = LinkedInLinkPreviewService.getInstance();
